import { Plus } from "lucide-react";

/* ── Internal modules ───────────────────────────────────────────────────── */
import { RBACAction } from "../../components/RBACButton";
import { TableToolbar } from "../../components/TableToolbar";
import { Button } from "../../components/ui/button";
import { Tip } from "../../components/ui/tooltip";
import { FilterDropdown } from "../itCompliance/components/FilterDropdown";

/* ── Relative imports ───────────────────────────────────────────────────── */
import { RBAC_MODULE } from "./schedulerColumns";

const SEARCH_MAX_LENGTH = 100;

interface SchedulerToolbarProps {
  search: string;
  onSearchChange: (value: string) => void;
  /** Called on Enter, magnifier click, or when cleared. Omit for search-as-you-type. */
  onSubmit?: (value: string) => void;
  /** Omit to hide the Add button (no create permission, or nothing to add). */
  onAdd?: () => void;
  /** Optional custom tooltip for the Add button. Defaults to "Add Scheduler". */
  addTooltip?: string;
  /** Tool names from the CMDB API. Pass to show a Tool filter after the search. */
  toolFilterOptions?: string[];
  /** Currently selected tool names (empty = All). */
  toolFilter?: string[];
  /** Called when the tool filter selection changes. */
  onToolFilterChange?: (values: string[]) => void;
  /** Pass both to show a Rows per page select before the Add button. */
  pageSize?: number;
  onPageSizeChange?: (n: number) => void;
}

export function SchedulerToolbar({
  search,
  onSearchChange,
  onSubmit,
  onAdd,
  addTooltip,
  toolFilterOptions,
  toolFilter = [],
  onToolFilterChange,
  pageSize,
  onPageSizeChange,
}: SchedulerToolbarProps) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-3">
        {/* Left group: tool filter */}
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {toolFilterOptions && toolFilterOptions.length > 0 && (
            <FilterDropdown
              label="Tool"
              options={toolFilterOptions}
              value={toolFilter}
              onChange={onToolFilterChange ?? (() => {})}
              searchable
              searchPlaceholder="Search"
              variant="pill"
            />
          )}
        </div>

        {/* Right: Add button */}
        {onAdd && (
          <RBACAction module={RBAC_MODULE} action="create">
            <Tip label={addTooltip ?? "Add Scheduler"} side="bottom">
              <Button
                variant="indigo"
                onClick={onAdd}
                className="h-9 shrink-0 gap-2 rounded-xl px-4 font-bold shadow-[0_4px_14px_rgba(79,70,229,0.3)]"
                aria-label={addTooltip ?? "Add Scheduler"}
              >
                <Plus className="size-4" />
                Add
              </Button>
            </Tip>
          </RBACAction>
        )}
      </div>
      <TableToolbar
        search={{
          value: search,
          onChange: onSearchChange,
          onSubmit,
          maxLength: SEARCH_MAX_LENGTH,
          placeholder: "Search",
        }}
        pageSize={pageSize}
        onPageSizeChange={pageSize !== undefined ? onPageSizeChange : undefined}
        pageSizeLabel="Rows per page"
      />
    </div>
  );
}
